import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BusinessLink {
  businessName: string;
  slug: string;
  url: string;
  createdAt: Date;
  filesReceived: number;
  totalSize: number;
  isActive: boolean;
  password?: string;
  maxFileSize: number;
  allowedTypes: string[];
}

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedAt: Date;
  uploaderName?: string;
  uploaderEmail?: string;
  fileType: string;
  downloadUrl: string;
}

const BusinessFileSharing: React.FC = () => {
  const [businessName, setBusinessName] = useState('');
  const [slug, setSlug] = useState('');
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);
  const [saved, setSaved] = useState(false);
  const [myLinks, setMyLinks] = useState<BusinessLink[]>([]);
  const [selectedLink, setSelectedLink] = useState<BusinessLink | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('create');
  const [linkSettings, setLinkSettings] = useState({
    requirePassword: false,
    password: '',
    maxFileSize: 100, // MB
    allowedTypes: ['*'],
    notifyOnUpload: true,
  });

  // Generate slug from business name
  useEffect(() => {
    if (businessName) {
      // Convert to URL-friendly slug
      const newSlug = businessName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(newSlug);

      // Check availability after debounce
      const timer = setTimeout(() => {
        if (newSlug.length >= 3) {
          checkAvailability(newSlug);
        }
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setSlug('');
      setIsAvailable(null);
    }
  }, [businessName]);

  // Check if slug is available
  const checkAvailability = async (slugToCheck: string) => {
    setChecking(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 300));

    // For demo, check against existing links
    const exists = myLinks.some(link => link.slug === slugToCheck);
    setIsAvailable(!exists && slugToCheck.length >= 3);
    setChecking(false);
  };

  // Create business link
  const createBusinessLink = async () => {
    if (!businessName || !slug || !isAvailable) return;

    const newLink: BusinessLink = {
      businessName,
      slug,
      url: `${window.location.origin}/files/${slug}`,
      createdAt: new Date(),
      filesReceived: 0,
      totalSize: 0,
      isActive: true,
      password: linkSettings.requirePassword ? linkSettings.password : undefined,
      maxFileSize: linkSettings.maxFileSize * 1024 * 1024,
      allowedTypes: linkSettings.allowedTypes,
    };

    // Save to localStorage for demo
    const existingLinks = JSON.parse(localStorage.getItem('businessLinks') || '[]');
    existingLinks.push(newLink);
    localStorage.setItem('businessLinks', JSON.stringify(existingLinks));

    setMyLinks([...myLinks, newLink]);
    setSaved(true);

    // Copy URL to clipboard
    navigator.clipboard.writeText(newLink.url);

    // Reset after 3 seconds
    setTimeout(() => {
      setSaved(false);
      setBusinessName('');
      setSlug('');
      setIsAvailable(null);
      setActiveTab('manage');
    }, 3000);
  };

  // Load saved links on mount
  useEffect(() => {
    const savedLinks = JSON.parse(localStorage.getItem('businessLinks') || '[]');
    setMyLinks(savedLinks.map((link: any) => ({
      ...link,
      createdAt: new Date(link.createdAt),
    })));
  }, []);

  // Simulate receiving files
  const simulateFileUpload = (link: BusinessLink) => {
    const newFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      fileName: `document_${Math.random().toString(36).substr(2, 6)}.pdf`,
      fileSize: Math.floor(Math.random() * 10) * 1024 * 1024,
      uploadedAt: new Date(),
      uploaderName: 'John Doe',
      uploaderEmail: 'john@example.com',
      fileType: 'application/pdf',
      downloadUrl: '#',
    };

    setUploadedFiles([...uploadedFiles, newFile]);

    // Update link stats
    const updatedLinks = myLinks.map(l => {
      if (l.slug === link.slug) {
        return {
          ...l,
          filesReceived: l.filesReceived + 1,
          totalSize: l.totalSize + newFile.fileSize,
        };
      }
      return l;
    });
    setMyLinks(updatedLinks);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">
            Business File Sharing
          </h1>
          <p className="text-xl text-gray-300">
            Create a simple, memorable link for clients to upload files to you
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-8">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'create'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Create Link
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'manage'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            My Links ({myLinks.length})
          </button>
        </div>

        {/* Create Tab */}
        {activeTab === 'create' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8 border border-gray-700"
          >
            <div className="space-y-6">
              {/* Business Name Input */}
              <div>
                <label className="block text-sm font-medium mb-3 text-gray-300">
                  Enter Your Business Name
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g., Acme Corporation"
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-lg"
                  autoFocus
                />
              </div>

              {/* Live URL Preview */}
              <AnimatePresence>
                {businessName && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg p-6 border border-blue-500/30">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-400">Your shareable link will be:</span>
                        {checking && (
                          <span className="text-xs text-yellow-400 animate-pulse">Checking availability...</span>
                        )}
                        {!checking && isAvailable === true && slug.length >= 3 && (
                          <span className="text-xs text-green-400 font-semibold">✓ Available!</span>
                        )}
                        {!checking && isAvailable === false && (
                          <span className="text-xs text-red-400 font-semibold">✗ Already taken</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 bg-gray-900/50 rounded-lg p-4">
                        <span className="text-gray-500">happyhumantools.com/files/</span>
                        <span className="text-white font-bold text-xl">{slug || '...'}</span>
                      </div>

                      {slug.length > 0 && slug.length < 3 && (
                        <p className="text-sm text-yellow-400 mt-2">
                          URL must be at least 3 characters
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Optional Settings */}
              {businessName && isAvailable && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-gray-300">Optional Settings</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Max File Size (MB)</label>
                      <input
                        type="number"
                        value={linkSettings.maxFileSize}
                        onChange={(e) => setLinkSettings({...linkSettings, maxFileSize: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-3 cursor-pointer mt-7">
                        <input
                          type="checkbox"
                          checked={linkSettings.requirePassword}
                          onChange={(e) => setLinkSettings({...linkSettings, requirePassword: e.target.checked})}
                          className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded"
                        />
                        <span className="text-sm text-gray-400">Require password</span>
                      </label>
                    </div>
                  </div>

                  {linkSettings.requirePassword && (
                    <input
                      type="password"
                      value={linkSettings.password}
                      onChange={(e) => setLinkSettings({...linkSettings, password: e.target.value})}
                      placeholder="Enter password"
                      className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg"
                    />
                  )}
                </motion.div>
              )}

              {/* Create Button */}
              <AnimatePresence>
                {businessName && isAvailable && !saved && (
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    onClick={createBusinessLink}
                    className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition transform hover:scale-[1.02]"
                  >
                    Create My Business Link
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Success Message */}
              <AnimatePresence>
                {saved && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="bg-green-500/20 border border-green-500 rounded-lg p-6 text-center"
                  >
                    <div className="text-2xl mb-2">✅</div>
                    <h3 className="text-xl font-bold text-green-400 mb-2">Link Created Successfully!</h3>
                    <p className="text-gray-300">Your link has been copied to clipboard</p>
                    <div className="mt-3 bg-gray-900/50 rounded p-3">
                      <code className="text-blue-400">{window.location.origin}/files/{slug}</code>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* Manage Tab */}
        {activeTab === 'manage' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {myLinks.length === 0 ? (
              <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-12 text-center border border-gray-700">
                <div className="text-5xl mb-4">📁</div>
                <h3 className="text-2xl font-bold mb-2">No Links Yet</h3>
                <p className="text-gray-400 mb-6">Create your first business link to start receiving files</p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold"
                >
                  Create Your First Link
                </button>
              </div>
            ) : (
              myLinks.map((link, index) => (
                <motion.div
                  key={link.slug}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-bold mb-1">{link.businessName}</h3>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          link.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {link.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-sm text-gray-400">
                          {link.filesReceived} files • {(link.totalSize / 1024 / 1024).toFixed(1)} MB
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => simulateFileUpload(link)}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-semibold transition"
                    >
                      Simulate Upload
                    </button>
                  </div>

                  <div className="bg-gray-900/50 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between">
                      <code className="text-blue-400">{link.url}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(link.url);
                        }}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm font-semibold transition"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <div className="text-white">{new Date(link.createdAt).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Max Size:</span>
                      <div className="text-white">{link.maxFileSize / 1024 / 1024} MB</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Password:</span>
                      <div className="text-white">{link.password ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BusinessFileSharing;